import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import "./patient_profile.css";
import SideBar from "../Components/SideBar.js";
import { API_BASE_URL } from "../config.js";
import PatientProfileMobile from "./patient_profile_mobile.js";

export default function PatientProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const role = location.state?.role || "doctor";

  const [patient, setPatient] = useState(null);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // مراقبة حجم الشاشة للتبديل التلقائي لنسخة الهاتف
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    // جلب بيانات المريض، الزيارات، وقائمة الأمراض معاً
    Promise.all([
      fetch(`${API_BASE_URL}/patients/${id}/`, { method: "GET", headers }),
      fetch(`${API_BASE_URL}/visits/?patient=${id}`, { method: "GET", headers }),
      fetch(`${API_BASE_URL}/diseases/`, { method: "GET", headers }) // جلب الأمراض
    ])
      .then(async ([patientRes, visitsRes, diseasesRes]) => {
        if (!patientRes.ok) {
          throw new Error("حدث خطأ أثناء جلب بيانات المريض.");
        }

        const patientData = await patientRes.json();
        
        // بناء خريطة (Dictionary) للأمراض: { id: disease_code }
        let diseasesMap = {};
        if (diseasesRes.ok) {
          const diseasesJson = await diseasesRes.json();
          const diseasesList = diseasesJson.results ? diseasesJson.results : diseasesJson;
          diseasesList.forEach(d => {
            diseasesMap[d.id] = d.disease_code;
          });
        }

        let visitsData = [];
        if (visitsRes.ok) {
          const visitsJson = await visitsRes.json();
          const rawVisits = visitsJson.results ? visitsJson.results : visitsJson;
          
          // إضافة حقل جديد للزيارة يحتوي على رمز المرض اعتماداً على الـ id
          visitsData = rawVisits.map(visit => ({
            ...visit,
            display_disease_code: diseasesMap[visit.disease] || visit.disease_code || "غير محدد"
          }));
        }

        setPatient(patientData);
        setVisits(visitsData);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setError(err.message);
        setLoading(false);
      });
  }, [id]);
  if (loading) {
    return (
      <div className="patient-profile-container" dir="rtl">
        {!isMobile && <SideBar role={role} />}
        <h2 style={{ padding: "50px", margin: "auto" }}>جاري التحميل...</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div className="patient-profile-container" dir="rtl">
        {!isMobile && <SideBar role={role} />}
        <h2 style={{ padding: "50px", color: "red", margin: "auto" }}>{error}</h2>
      </div>
    );
  }

  if (!patient) return null;

  // عرض نسخة الهاتف إذا كانت الشاشة صغيرة
  if (isMobile) {
    return (
      <PatientProfileMobile 
        patient={patient} 
        visits={visits} 
        onBack={() => navigate(-1)} 
        onAddVisit={() => navigate("/add-state", { state: { patientId: patient.id, patientName: patient.name } })} 
        onVisitClick={(visitId) => navigate(`/visit/${visitId}`, { state: { visitId, patient: patient, role } })}
      />
    );
  }

  // عرض نسخة سطح المكتب
  return (
    <div className="patient-profile-container" dir="rtl">
      <SideBar role={role} />
      <div className="patient-profile-left">
        <div className="patient-profile-bg-watermark"></div>
        {/* Header */}
        <div className="patient-profile-header">
          <h1 className="patient-profile-name">{patient.name}</h1>
        </div>
        
        {/* Personal Info */}
        <div className="patient-profile-info">
          <h3 className="patient-profile-info-title">المعلومات الشخصية</h3>
          
          <div className="patient-profile-info-row">
            <i className="fa-solid fa-id-card patient-profile-icon"></i>
            <span className="patient-profile-info-span">الرقم الوطني :</span>
            <span className="patient-profile-value">{patient.national_number}</span>
          </div>
          
          <div className="patient-profile-info-row">
            <i className="fa-solid fa-calendar patient-profile-icon"></i>
            <span className="patient-profile-info-span">تاريخ الميلاد :</span>
            <span className="patient-profile-info-span">{patient.birth_date}</span>
          </div>
          
          
          
          <div className="patient-profile-info-row">
            <i className="fa-solid fa-mars-and-venus patient-profile-icon"></i>
            <span className="patient-profile-info-span">الجنس :</span>
            <span className="patient-profile-info-span">{patient.gender}</span>
          </div>
          
          <div className="patient-profile-info-row">
            <i className="fa-solid fa-location-dot patient-profile-icon"></i>
            <span className="patient-profile-info-span">إحداثيات السكن :</span>
            <span className="patient-profile-info-span">{patient.residence_lat || 'غير متوفر'}</span>
          </div>
          
          <div className="patient-profile-info-row">
            <i className="fa-solid fa-briefcase patient-profile-icon"></i>
            <span className="patient-profile-info-span">إحداثيات العمل :</span>
            <span className="patient-profile-info-span">{patient.work_lat || 'غير متوفر'}</span>
          </div>
        </div>
        
        {/* Visits */}
       <div className="patient-profile-visits">
          <h3 className="patient-profile-visits-title">
              الزيارات السابقة ({visits.length})
          </h3>
          {visits.length > 0 ? (
            visits.map((visit, index) => (
              <div
                key={index}
                className="patient-profile-visit-card"
              onClick={() =>
                  navigate(`/visit/${visit.id}`, {
                    state: { visitId: visit.id, patient: patient, role }
                  })
                }
                style={{ cursor: "pointer" }}
              >
                <div className="patient-profile-visit-right">
                <h4>رقم الزيارة : {visit.id}</h4>
                {/* استخدام الحقل المدمج الجديد هنا */}
                <p>رمز المرض : {visit.display_disease_code}</p>
                <p>تاريخ الزيارة : {visit.diagnosis_date || "غير محدد"}</p>
              </div>
              </div>
            ))
          ) : (
            <p style={{ padding: "10px" }}>لا توجد زيارات سابقة.</p>
          )}
        </div>
        
        {/* Add State Button */}
        <button
          className="patient-profile-visit-btn"
          onClick={() =>
            navigate("/add-state", {
              state: { patientId: patient.id, patientName: patient.name },
            })
          }
        >
          <i className="fa-solid fa-plus"></i>
        </button>
      </div>
    </div>
  );
}